/* eslint-disable @typescript-eslint/no-explicit-any */

import IconMinus from '@assets/icons/minus.svg?react';
import IconPlus from '@assets/icons/plus.svg?react';

import IconEdit from '@assets/icons/edit.svg?react';
import IconDelete from '@assets/icons/delete.svg?react';
import {
 Box,
 Button,
 Card,
 CardContent,
 Collapse,
 IconButton,
 Typography,
 useTheme,
} from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import EditFields from './EditFields';
import useEditArray from './useEditArray';
import { displayValueByType } from '@/utils/structureUtils';
import { useConfirmationDialog } from '@/contexts/useConfirmationDialog';

const EditArray = ({
 arrayKey,
 arrayTranslation,
}: {
 arrayKey: string;
 arrayTranslation: string;
}) => {
 const { t } = useTranslation();
 const th = useTheme();
 const { openDialog: openConfirmationDialog } = useConfirmationDialog();

 const {
  onSubmit,
  formMethods,
  loading,
  submitDisabled,
  fieldsWithData,
  handleEdit,
  handleCancel,
  handleDelete,
  showCreateForm,
  currentItem,
  editingId,
  handleCreate,
  currentArrayFieldStructure,
 } = useEditArray({ arrayKey });

 const handleDeleteConfirm = (id: number) => {
  openConfirmationDialog({
   title: t('global.delete'),
   message: t('global.deleteMessage'),
   onConfirm: () => handleDelete(id),
  });
 };

 const renderItemEditForm = (isEdit = false) => (
  <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
   <CardContent
    sx={{
     backgroundColor: th.palette.kemek.lightBlue,
     px: 1,
     pt: 2,
     pb: '8px !important',
    }}
   >
    <Box component="form" onSubmit={formMethods.handleSubmit(onSubmit)}>
     <EditFields fieldsWithData={fieldsWithData} type="array" />
     <Box
      sx={{
       display: 'flex',
       justifyContent: 'space-between',
       gap: 1,
       px: 1,
       mt: '8px',
       minHeight: '24px',
      }}
     >
      <Button
       sx={{ p: 0 }}
       size="small"
       variant="text"
       color="inherit"
       onClick={handleCancel}
       startIcon={<IconMinus style={{ color: th.palette.kemek.blue }} />}
      >
       {t('global.cancel')}
      </Button>
      <Button
       sx={{ p: 0 }}
       size="small"
       variant="text"
       color="primary"
       type="submit"
       disabled={submitDisabled}
       endIcon={
        <IconPlus
         style={{
          color: submitDisabled ? th.palette.kemek.gray : th.palette.kemek.blue,
         }}
        />
       }
      >
       {isEdit ? t('global.save') : t('global.create')}
      </Button>
     </Box>
    </Box>
   </CardContent>
  </Card>
 );

 const renderItem = (arrayItem: any) => {
  const isEditing = editingId === arrayItem.Id;
  if (isEditing) {
   return <Box key={arrayItem.Id}>{renderItemEditForm(true)}</Box>;
  }

  return (
   <Card key={arrayItem.Id} sx={{ border: '1px solid #f0f0f0' }}>
    <CardContent sx={{ py: 1, px: '12px', '&:last-child': { pb: '12px' } }}>
     <Box
      sx={{
       display: 'flex',
       justifyContent: 'space-between',
       alignItems: 'flex-start',
      }}
     >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
       {fieldsWithData.map((field) => (
        <Box
         key={field.Name}
         sx={{
          // mb: 0.5,
          wordBreak: 'break-all',
          fontSize: '12px',
          // display: 'flex',
          // alignItems: 'center',
          // gap: 1,
         }}
        >
         <Typography
          sx={{ fontWeight: 600, display: 'inline', fontSize: '12px' }}
         >
          {field.Translation}:{' '}
         </Typography>
         <Typography sx={{ display: 'inline', fontSize: '12px' }}>
          {displayValueByType(arrayItem[field.Name], field)}
         </Typography>
        </Box>
       ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
       <IconButton
        onClick={() => handleEdit(arrayItem.Id)}
        sx={{ color: 'primary.main' }}
        disabled={!!loading.updateItem}
       >
        <IconEdit style={{ color: th.palette.kemek.darkestGray }} />
       </IconButton>

       <IconButton
        onClick={() => handleDeleteConfirm(arrayItem.Id)}
        sx={{ color: 'error.main' }}
        disabled={!!loading.deleteItem}
       >
        <IconDelete style={{ color: th.palette.kemek.red }} />
       </IconButton>
      </Box>
     </Box>
    </CardContent>
   </Card>
  );
 };

 return (
  <FormProvider {...formMethods}>
   <Box
    sx={{
     display: 'flex',
     justifyContent: 'space-between',
     alignItems: 'center',
     mb: 2,
    }}
   >
    <Typography
     sx={{
      fontWeight: 500,
      fontSize: '14px',
      color: th.palette.kemek.darkGray,
     }}
    >
     {arrayTranslation}
    </Typography>

    <IconButton
     color="primary"
     onClick={handleCreate}
     disabled={showCreateForm || editingId !== null || !!loading.getItem}
    >
     <IconPlus />
    </IconButton>
   </Box>

   <Collapse in={showCreateForm}>{renderItemEditForm(false)}</Collapse>

   {currentItem?.length === 0 ? (
    <Box
     sx={{
      textAlign: 'center',
      p: '12px',
      backgroundColor: th.palette.kemek.lightBlue,
     }}
    >
     <Typography variant="body2" color="text.secondary">
      {t('global.noItemsFound')}
     </Typography>
    </Box>
   ) : (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
     {currentItem?.map(renderItem)}
    </Box>
   )}
  </FormProvider>
 );
};

export default EditArray;
